/* The studio presents the existing Shopify options and submits the original theme form. */
if (!customElements.get("pp-product-studio")) {
  class ProductStudio extends HTMLElement {
    connectedCallback() {
      this.main = this.closest("product-info");
      this.picker = this.querySelector("variant-picker");
      this.summary = this.querySelector("[data-studio-summary]");
      this.optionSlot = this.querySelector("[data-studio-options]");
      this.inlineSlot = this.querySelector("[data-studio-inline-options]");
      this.submit = this.main?.querySelector('product-form [name="add"]');
      this.form = this.submit?.form;
      if (!this.picker || !this.form || !this.inlineSlot || !this.optionSlot || !this.querySelector("[data-studio-status]")) return;

      this.events = new AbortController();
      const { signal } = this.events;
      this.addEventListener("click", (event) => {
        if (event.target.closest("[data-studio-guide]")) this.openGuide();
        if (event.target.closest("[data-studio-retry]")) this.retry();
        const color = event.target.closest("[data-studio-color]");
        if (color) this.chooseColor(color.dataset.studioColor);
      }, { signal });
      this.addEventListener("change", (event) => {
        if (event.target.closest("variant-picker")) this.beginSelection();
      }, { signal, capture: true });

      // The native theme replaces only <variant-picker> after a variant request.
      this.placeOptions([...this.picker.querySelectorAll(".variant-picker__field-wrapper")]);
      this.optionSlot.append(this.picker);
      this.optionObserver = new MutationObserver(() => this.sync());
      this.optionObserver.observe(this.optionSlot, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "hidden"] });
      document.addEventListener("variant:changed", () => this.sync(), { signal });

      this.summary.hidden = false;
      this.dataset.ready = "true";
      this.main.dataset.studioReady = "true";
      this.sync();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.optionObserver?.disconnect();
      clearTimeout(this.feedbackTimer);
      clearTimeout(this.retryTimer);
      if (this.main) delete this.main.dataset.studioReady;
    }

    get currentPicker() {
      // Zest inserts the new picker before the old one, hiding the old node for 500ms.
      // Only the active picker can confirm a choice or supply its labels/swatches.
      return [...this.optionSlot.querySelectorAll("variant-picker")]
        .find(picker => !picker.hidden && picker.style.display !== "none");
    }

    get fields() {
      return [...(this.currentPicker?.querySelectorAll(".variant-picker__field-wrapper") || [])];
    }

    selectedValue(field) {
      return field?.querySelector("input:checked, select option:checked")?.value || "";
    }

    placeOptions(fields) {
      const other = fields.filter(field => field.dataset.ppOptionKind !== "color");
      this.querySelector("[data-studio-choice]").hidden = other.length === 0;
      this.querySelector("[data-studio-fit]").hidden = !fields.some(field => field.dataset.ppOptionKind === "size")
        || !this.querySelector("[data-studio-fit] .fit-indicator");
      this.querySelector("[data-studio-option-label]").textContent = other[0]?.querySelector("[data-option-name]")?.dataset.optionName || "Størrelse";
      for (const [index, field] of other.entries()) {
        const name = field.querySelector("[data-option-name]")?.dataset.optionName || "Variant";
        field.dataset.ppPrimaryOption = String(index === 0);
        field.setAttribute("role", "group");
        field.setAttribute("aria-label", name);
        for (const input of field.querySelectorAll('input[type="radio"]')) {
          const soldOut = input.closest(".variant-picker__option")?.classList.contains("variant-picker__option--soldout");
          input.setAttribute("aria-label", input.value + (soldOut ? ", udsolgt i det valgte udvalg" : ""));
        }
        for (const select of field.querySelectorAll("select")) select.setAttribute("aria-label", name);
      }
    }

    sync() {
      if (!this.isConnected) return;
      const picker = this.currentPicker;
      // This picker has no exit animation. Remove Zest's hidden transition remnants now.
      for (const previous of this.optionSlot.querySelectorAll("variant-picker")) {
        if (previous !== picker && previous.style.display === "none") previous.remove();
      }
      const color = this.fields.find(field => field.dataset.ppOptionKind === "color");
      this.placeOptions(this.fields);
      const colorName = this.selectedValue(color);
      const variant = JSON.parse(this.currentPicker?.querySelector("[data-selected-variant]")?.textContent || "null");
      const formId = this.form.querySelector('[name="id"]')?.value;
      if (this.pending && picker && picker !== this.pendingPicker
        && picker.selectedOptionValues.join(",") === this.pendingValues
        && String(variant?.id || "") === formId) {
        this.pending = false;
        clearTimeout(this.feedbackTimer);
        clearTimeout(this.retryTimer);
        this.removeAttribute("aria-busy");
        delete this.main.dataset.studioPending;
        delete this.main.dataset.studioWaiting;
        this.querySelector("[data-studio-status]").textContent = "";
        this.querySelector("[data-studio-retry]").hidden = true;
      }
      this.querySelector("[data-studio-color-name]").textContent = colorName;
      this.querySelector(".pp-studio-heading").hidden = !color;
      this.querySelector("[data-studio-guide]").hidden = !this.currentPicker?.querySelector(".open-size-chart:not(.foxkit-hidden)");
      this.syncColors(color);
    }

    syncColors(field) {
      const rail = this.querySelector("[data-studio-colors]");
      const inputs = [...(field?.querySelectorAll('input[type="radio"]') || [])];
      rail.hidden = inputs.length === 0;
      const identity = JSON.stringify(inputs.map(input => input.dataset.optionValueId));
      if (rail.dataset.options !== identity) {
        rail.replaceChildren(...inputs.map(input => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "pp-studio-color";
          button.dataset.studioColor = input.dataset.optionValueId;
          const image = document.createElement("span");
          image.className = "pp-studio-portrait";
          image.setAttribute("aria-hidden", "true");
          const name = document.createElement("span");
          name.className = "pp-studio-color-caption";
          name.textContent = input.value;
          button.append(image, name);
          return button;
        }));
        rail.dataset.options = identity;
      }
      for (const input of inputs) {
        const button = [...rail.children].find(button => button.dataset.studioColor === input.dataset.optionValueId);
        const label = input.labels?.[0];
        const soldOut = input.closest(".variant-picker__option")?.classList.contains("variant-picker__option--soldout");
        button.setAttribute("aria-pressed", String(input.checked));
        button.setAttribute("aria-label", input.value + (soldOut ? ", udsolgt i det valgte udvalg" : ""));
        button.dataset.soldOut = String(!!soldOut);
        // Reuse the merchant's existing image/color, without guessing from a color name.
        const image = button.querySelector(".pp-studio-portrait");
        const style = label?.style.cssText;
        if (style) {
          if (image.style.cssText !== style) image.style.cssText = style;
          image.textContent = "";
        } else if (!image.style.cssText) {
          image.textContent = input.value;
        }
      }
    }

    chooseColor(valueId) {
      const input = [...(this.currentPicker?.querySelectorAll('input[type="radio"]') || [])]
        .find(input => input.dataset.optionValueId === valueId);
      if (input && !input.disabled) {
        if (!input.checked) input.click();
        else if (this.pending) input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    beginSelection() {
      this.pending = true;
      this.pendingPicker = this.currentPicker;
      this.pendingValues = this.currentPicker.selectedOptionValues.join(",");
      this.submit.disabled = true;
      this.setAttribute("aria-busy", "true");
      this.main.dataset.studioPending = "true";
      delete this.main.dataset.studioWaiting;
      this.querySelector("[data-studio-status]").textContent = "";
      this.querySelector("[data-studio-retry]").hidden = true;
      // Fast confirmations keep their layout; longer requests still explain the wait.
      clearTimeout(this.feedbackTimer);
      this.feedbackTimer = setTimeout(() => {
        if (!this.pending) return;
        this.main.dataset.studioWaiting = "true";
        this.querySelector("[data-studio-status]").textContent = "Opdaterer dit valg…";
      }, 400);
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => {
        if (!this.pending) return;
        this.querySelector("[data-studio-status]").textContent = "Valget kunne ikke opdateres. Prøv igen.";
        this.querySelector("[data-studio-retry]").hidden = false;
      }, 10000);
    }

    retry() {
      const input = this.currentPicker?.querySelector("input:checked, select");
      input?.dispatchEvent(new Event("change", { bubbles: true }));
    }

    openGuide() {
      const opener = this.currentPicker?.querySelector(".open-size-chart:not(.foxkit-hidden)");
      const button = opener?.matches("button") ? opener : opener?.querySelector("button");
      button?.click();
    }

  }
  customElements.define("pp-product-studio", ProductStudio);
}

/* Keep native information panels usable without exposing their collapsed links. */
if (!customElements.get("pp-product-details")) {
  customElements.define("pp-product-details", class extends HTMLElement {
    connectedCallback() {
      this.main = this.closest("product-info");
      if (!this.main) return;
      this.events = new AbortController();
      this.contents = [...this.main.querySelectorAll("collapsible-tab [data-content]")];
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.main, {
        subtree: true, attributes: true, attributeFilter: ["aria-hidden", "hidden"],
      });
      this.main.addEventListener("click", (event) => {
        const button = event.target.closest("[data-pp-shipping-target]");
        if (!button) return;
        const panel = this.main.querySelector(`[data-block-id="${CSS.escape(button.dataset.ppShippingTarget)}"]`);
        const trigger = panel?.querySelector("[data-trigger]");
        if (!trigger) return;
        if (panel.getAttribute("open") !== "true") trigger.click();
        panel.scrollIntoView({ block: "start", behavior: "auto" });
        trigger.focus({ preventScroll: true });
      }, { signal: this.events.signal });
      this.sync();
      this.dataset.ready = "true";
    }

    sync() {
      for (const content of this.contents) {
        content.inert = content.hidden || content.getAttribute("aria-hidden") === "true";
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      for (const content of this.contents || []) content.inert = false;
    }
  });
}
